import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, success: false, message: "", plan: null, endDate: null });
  const confirmedRef = useRef(false);

  useEffect(() => {
    if (confirmedRef.current) return;
    confirmedRef.current = true;

    const id = searchParams.get("id");
    const clientTxId = searchParams.get("clientTransactionId") || searchParams.get("clientTxId");

    if (!id || !clientTxId) {
      setState({ loading: false, success: false, message: "Datos de pago faltantes en la URL." });
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setState({ loading: false, success: false, message: "Tu sesión expiró. Vuelve a iniciar sesión." });
      return;
    }

    axios.post(`${API}/payments/payphone/confirm`,
      { id: parseInt(id, 10), clientTransactionId: clientTxId },
      { headers: { Authorization: `Bearer ${token}` } }
    ).then(({ data }) => {
      setState({
        loading: false,
        success: data.success,
        message: data.message,
        plan: data.plan,
        endDate: data.end_date,
      });
    }).catch((err) => {
      setState({
        loading: false,
        success: false,
        message: err.response?.data?.detail || "Error al confirmar el pago.",
      });
    });
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-24" data-testid="payment-result-page">
        {state.loading && (
          <Card className="bg-card/50 border-white/10">
            <CardContent className="py-12 text-center">
              <Loader2 className="w-12 h-12 text-[#E50914] animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Confirmando tu pago...</h2>
              <p className="text-white/60">Verificando la transacción con PayPhone.</p>
            </CardContent>
          </Card>
        )}

        {!state.loading && state.success && (
          <Card className="bg-gradient-to-br from-black via-black to-emerald-900/20 border-emerald-500/40 shadow-[0_0_60px_rgba(16,185,129,0.15)]" data-testid="payment-success">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-3">¡Pago confirmado!</h2>
              <p className="text-emerald-300 mb-2 text-lg">{state.message}</p>
              {state.plan && (
                <p className="text-white/80">
                  Plan: <span className="font-bold text-[#E50914]">
                    {state.plan === "monthly" ? "PRO Mensual" : "PRO Anual"}
                  </span>
                </p>
              )}
              {state.endDate && (
                <p className="text-white/60 text-sm mt-2">
                  Activo hasta: <span className="font-semibold text-white">
                    {new Date(state.endDate).toLocaleDateString("es-ES")}
                  </span>
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <Button onClick={() => navigate("/cursos")} className="bg-[#E50914] hover:bg-[#E50914]/90 text-white" data-testid="go-to-courses-btn">
                  Ver Cursos
                </Button>
                <Button onClick={() => navigate("/mis-datos")} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Mi Perfil
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!state.loading && !state.success && (
          <Card className="bg-card/50 border-red-500/40" data-testid="payment-failure">
            <CardContent className="py-12 text-center">
              <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-3">Pago no completado</h2>
              <p className="text-white/70 mb-2">{state.message}</p>
              <p className="text-white/50 text-sm mb-6">Si el problema persiste, contáctanos por WhatsApp.</p>
              <Button onClick={() => navigate("/contaflix-pro")} className="bg-[#E50914] hover:bg-[#E50914]/90 text-white">
                Intentar de nuevo
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PaymentResult;
